import { Components, Theme, buttonClasses } from '@mui/material';
import { toPixelString } from '../../utils';

// Padding used in buttons (in pixels) for each size option
const buttonPadding = {
  small: [2, 12],
  medium: [6, 16],
};

/**
 * Component overrides (i.e. global styles, default props) applied to the MUI
 * Button to make it match the design system.
 *
 * See: https://mui.com/material-ui/api/button/#css
 */
export const MuiButton: Components<Theme>['MuiButton'] = {
  defaultProps: {
    disableElevation: true,
  },
  styleOverrides: {
    root: ({ theme }) => ({
      ...theme.typography.button,
    }),

    sizeSmall: {
      padding: toPixelString(buttonPadding.small),
      '.MuiSvgIcon-root': {
        width: '16px',
        height: '16px',
      },
    },

    sizeMedium: {
      padding: toPixelString(buttonPadding.medium),
      '.MuiSvgIcon-root': {
        width: '20px',
        height: '20px',
      },
    },

    // For outlined buttons, we need to subtract 1px for the border to ensure
    // they're the same size as the other variants
    outlinedSizeSmall: {
      padding: toPixelString(buttonPadding.small.map((p) => p - 1)),
    },
    outlinedSizeMedium: {
      padding: toPixelString(buttonPadding.medium.map((p) => p - 1)),
    },

    // This is what our "primary" variant in chrono-ui maps to
    containedPrimary: ({ theme }) => ({
      [`&:hover`]: {
        backgroundColor: theme.palette.primary.highContrast,
      },

      [`&.${buttonClasses.disabled}`]: {
        backgroundColor: theme.palette.background.hover,
        color: theme.palette.text.disabled,
      },
    }),
    // This is what our "outlined" variant in chrono-ui maps to
    outlinedSecondary: ({ theme }) => ({
      backgroundColor: theme.palette.background.container,
      color: theme.palette.text.primary,
      borderColor: theme.palette.border.prominent,

      [`&:hover`]: {
        backgroundColor: theme.palette.background.hover,
        borderColor: theme.palette.text.secondary,
      },

      [`&.${buttonClasses.disabled}`]: {
        color: theme.palette.text.disabled,
        borderColor: theme.palette.border.prominent,
      },
    }),

    // This is what our "text" variant in chrono-ui maps to
    textPrimary: ({ theme }) => ({
      color: theme.palette.text.secondary,

      [`&:hover`]: {
        color: theme.palette.text.primary,
        backgroundColor: 'rgba(0, 0, 0, 0.04)',
      },

      [`&.${buttonClasses.disabled}`]: {
        color: theme.palette.text.disabled,
      },
    }),

    // This is what our "danger primary" variant in chrono-ui maps to
    containedError: ({ theme }) => ({
      [`&:hover`]: {
        backgroundColor: theme.palette.status.error.highContrast,
      },
      [`&.${buttonClasses.disabled}`]: {
        backgroundColor: theme.palette.background.hover,
        color: theme.palette.text.disabled,
      },
    }),

    // This is what our "danger outlined" variant in chrono-ui maps to
    outlinedError: ({ theme }) => ({
      borderColor: theme.palette.status.error.main,
      background: theme.palette.background.container,

      [`&:hover`]: {
        backgroundColor: theme.palette.status.error.lowContrast,
        borderColor: theme.palette.status.error.main,
      },

      [`&.${buttonClasses.disabled}`]: {
        color: theme.palette.text.disabled,
        borderColor: theme.palette.border.prominent,
      },
    }),

    // This is what our "danger text" variant in chrono-ui maps to
    textError: ({ theme }) => ({
      color: theme.palette.status.error.main,
      [`&:hover`]: {
        backgroundColor: theme.palette.status.error.lowContrast,
      },

      [`&.${buttonClasses.disabled}`]: {
        color: theme.palette.text.disabled,
      },
    }),
  },
};
